import React, { useMemo } from "react";
import axios from "axios";
import { useSortBy, useTable } from "react-table";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import { connect } from "react-redux";
import { toCurrency } from "@/shared/helpers";
import { Sorting } from "@/shared/apiTableHelper";
import { TABLE_COLUMNS } from "./columns";

const queryClient = new QueryClient();

// The App.jsx interceptor flashes the server message and resolves a failed
// request as { status, handled: true }; surface it as a query error so a
// denied or failed request never renders as an empty, $0.00 report.
const fetchReport = (url) =>
  axios.get(url).then((response) => {
    if (response.handled) {
      throw { status: response.status };
    }
    return response.data;
  });

const errorMessage = (error) =>
  (error.status ?? error.response?.status) === 403
    ? "You do not have access to this report."
    : "The pending holds report could not be loaded.";

/**
 * Unlike the other manager reports, this one is intentionally NOT
 * server-paginated: the ticket requires the displayed per-program and grand
 * totals to equal the sum of the listed rows, which only holds if every
 * currently pending/on-hold row is on screen at once. In practice this list
 * is a live queue (held deposits plus redemptions awaiting review), not
 * a historical log, so it stays small.
 */
const DataTable = ({ organization, program }) => {
  const apiUrl = `/organization/${organization.id}/program/${program.id}/report/pending-holds`;

  const { isLoading, error, data, isSuccess } = useQuery(
    ["pending-holds-report", apiUrl],
    () => fetchReport(apiUrl),
    { staleTime: 30000, retry: false }
  );

  const columns = useMemo(() => TABLE_COLUMNS, []);
  const rows = data?.data ?? [];
  const programTotals = data?.program_totals ?? [];
  const grandTotal = data?.grand_total ?? 0;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: rows,
      autoResetSortBy: false,
    },
    useSortBy
  );

  if (isLoading) {
    return <p>Loading...</p>;
  }

  if (error) {
    return <p>{errorMessage(error)}</p>;
  }

  if (!isSuccess) {
    return null;
  }

  return (
    <div className="table react-table report-table">
      <table {...getTableProps()} className="table table-striped report-table">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}
                  {column.isSorted ? <Sorting column={column} /> : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="table table--bordered" {...getTableBodyProps()}>
          {tableRows.length === 0 && (
            <tr>
              <td colSpan={columns.length}>No funds are currently pending or on hold.</td>
            </tr>
          )}
          {tableRows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="pending-holds-report__totals mt-4">
        <h4>Totals by Program</h4>
        <table className="table table-striped report-table">
          <thead>
            <tr>
              <th>Program</th>
              <th>Total Held</th>
            </tr>
          </thead>
          <tbody className="table table--bordered">
            {programTotals.length === 0 && (
              <tr>
                <td colSpan={2}>No held amounts.</td>
              </tr>
            )}
            {programTotals.map((row) => (
              <tr key={row.program_id}>
                <td>{row.program_name}</td>
                <td>{toCurrency(row.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Grand Total</th>
              <th>{toCurrency(grandTotal)}</th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const TableWrapper = ({ organization, program, client = queryClient }) => {
  if (!organization || !program) return "Loading...";
  return (
    <QueryClientProvider client={client}>
      <DataTable organization={organization} program={program} />
    </QueryClientProvider>
  );
};

const mapStateToProps = (state) => ({
  program: state.program,
  organization: state.organization,
});

export default connect(mapStateToProps)(TableWrapper);
